import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { useTranslation } from 'react-i18next';
import { GlassButton, GlassDialog } from '../ui';
import { formatMergeValue } from '../../api/game-data/optimistic-lock';
import { BORDER_COLORS, TEXT_COLORS } from '../../utils/colors';
import { DIALOG } from '../../utils/responsive';
import { toggleGroup } from '../../theme/sxPresets';
import { formatDateFromServer } from '../../utils/timezone';
import { ConflictRequest, GameDataPayload, TranslateFn } from '../../types/game-data.types';

type Side = 'mine' | 'theirs';

export interface ConflictResolutionDialogProps {
  /** `null` — диалога нет. Приходит из перехватчика 409 через `GameDataConflictProvider`. */
  request: ConflictRequest | null;
  onCancel: () => void;
  onSubmit: (payload: GameDataPayload) => void;
}

/**
 * Разрешение конфликта правок: спрашиваем ТОЛЬКО по разошедшимся полям.
 *
 * Всё, что каждая из сторон не трогала, уже слито автоматически (`optimistic-lock.ts`) —
 * человеку показывать это незачем, и «весь объект против всего объекта» тоже: он не смог бы
 * понять, что именно разошлось.
 *
 * Диалог общий для всех таблиц игровых данных: подписи полей берутся из `MergeSpec`.
 */
const ConflictResolutionDialog: React.FC<ConflictResolutionDialogProps> = ({
  request,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const translate = useMemo<TranslateFn>(
    () => (key, options) => t(key, options as never) as unknown as string,
    [t],
  );

  const [choices, setChoices] = useState<Record<string, Side>>({});
  const [submitting, setSubmitting] = useState(false);

  // Новый конфликт — начинаем с «оставить моё» по каждому полю: человек пришёл сюда сохранять
  // свою правку, а не чужую.
  useEffect(() => {
    if (!request) return;
    const initial: Record<string, Side> = {};
    request.conflicts.forEach((conflict) => {
      initial[conflict.key] = 'mine';
    });
    setChoices(initial);
    setSubmitting(false);
  }, [request]);

  if (!request) return null;

  const { conflicts, merged, theirs, spec } = request;
  const author = spec.updatedBy?.(theirs);
  const rowLabel = spec.rowLabel?.(theirs);
  const theirName = author?.username || t('gameData:conflict.theirsUnknown');

  const setAll = (side: Side) => {
    const next: Record<string, Side> = {};
    conflicts.forEach((conflict) => {
      next[conflict.key] = side;
    });
    setChoices(next);
  };

  const handleSubmit = () => {
    const payload: GameDataPayload = { ...merged };
    conflicts.forEach((conflict) => {
      payload[conflict.key] = choices[conflict.key] === 'theirs' ? conflict.theirs : conflict.mine;
    });
    setSubmitting(true);
    onSubmit(payload);
  };

  return (
    <GlassDialog open onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: DIALOG.titleFontSize, display: 'flex', alignItems: 'center', gap: 1 }}>
        <MergeTypeIcon sx={{ color: TEXT_COLORS.MUTED }} />
        {t('gameData:conflict.title', { entity: t(spec.entityKey) })}
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: DIALOG.contentFontSize, color: TEXT_COLORS.MUTED, mb: 0.5 }}>
          {t('gameData:conflict.intro', {
            name: theirName,
            n: conflicts.length,
          })}
        </Typography>

        {(rowLabel || author?.at) && (
          <Typography sx={{ fontSize: DIALOG.helperTextFontSize, color: TEXT_COLORS.DISABLED, mb: 2 }}>
            {[rowLabel, author?.at ? formatDateFromServer(author.at) : null].filter(Boolean).join(' · ')}
          </Typography>
        )}

        {conflicts.length > 1 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button size="small" onClick={() => setAll('mine')} sx={{ color: TEXT_COLORS.MUTED }}>
              {t('gameData:conflict.allMine')}
            </Button>
            <Button size="small" onClick={() => setAll('theirs')} sx={{ color: TEXT_COLORS.MUTED }}>
              {t('gameData:conflict.allTheirs')}
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {conflicts.map((conflict) => {
            const format = conflict.format ?? formatMergeValue;
            return (
              <Box
                key={conflict.key}
                sx={{
                  border: `1px solid ${BORDER_COLORS.DEFAULT}`,
                  borderRadius: 2,
                  p: { xs: 1, sm: 1.5 },
                }}
              >
                <Typography
                  sx={{ fontSize: DIALOG.contentFontSize, fontWeight: 600, color: TEXT_COLORS.PRIMARY, mb: 1 }}
                >
                  {t(conflict.labelKey)}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={choices[conflict.key] ?? 'mine'}
                  onChange={(_, value) => {
                    if (!value) return;
                    setChoices((prev) => ({ ...prev, [conflict.key]: value as Side }));
                  }}
                  sx={{
                    ...toggleGroup,
                    flexDirection: { xs: 'column', sm: 'row' },
                    // Отдельный селектор, а не `& .MuiToggleButton-root` из пресета: иначе
                    // объектный спред затёр бы его целиком вместе с цветами.
                    '& .MuiToggleButtonGroup-grouped': {
                      display: 'block',
                      textAlign: 'left',
                      // Значение бывает длинным (список цен) — рвём по словам, а не в одну строку.
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    },
                  }}
                >
                  <ToggleButton value="mine">
                    <Typography component="span" sx={{ display: 'block', fontSize: DIALOG.helperTextFontSize, color: TEXT_COLORS.DISABLED }}>
                      {t('gameData:conflict.mine')}
                    </Typography>
                    {format(conflict.mine, translate)}
                  </ToggleButton>
                  <ToggleButton value="theirs">
                    <Typography component="span" sx={{ display: 'block', fontSize: DIALOG.helperTextFontSize, color: TEXT_COLORS.DISABLED }}>
                      {t('gameData:conflict.theirs', { name: theirName })}
                    </Typography>
                    {format(conflict.theirs, translate)}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          {t('gameData:conflict.cancel')}
        </Button>
        <GlassButton onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : t('gameData:conflict.save')}
        </GlassButton>
      </DialogActions>
    </GlassDialog>
  );
};

export default ConflictResolutionDialog;
